import { Loader } from '@googlemaps/js-api-loader';
import debounce from 'debounce-promise';
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, FieldError, FieldPath, FieldValues } from 'react-hook-form';

import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import { filterLocationsToPostalCodeOrLower } from '@utils/locationUtils';

export type Location = {
  address: string | null;
  placeId: string | null;
};

type Props<FormData extends FieldValues> = Pick<
  ComponentProps<typeof AsyncSelect>,
  'components' | 'placeholder' | 'size'
> & {
  control: Control<FormData>;
  error: FieldError | undefined;
  label?: string | ReactNode;
  location: Location;
  name: FieldPath<FormData>;
  noResults?: string;
  required?: boolean;
  subLabel?: string | ReactNode;
};

const LocationAutocompleteInput = <FormData extends FieldValues>({
  control,
  error,
  label,
  location,
  name,
  noResults = '',
  required,
  subLabel,
  ...asyncSelectProps
}: Props<FormData>) => {
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Option | null>(
    location.placeId
      ? {
          label: location.address ?? '',
          value: location.placeId ?? ''
        }
      : null
  );

  const googleOption: Option = useMemo(
    () => ({
      disabled: true,
      imageName: 'poweredByGoogle',
      label: '',
      value: 'poweredByGoogleImage'
    }),
    []
  );

  const handleSetService = () => {
    if (!window.google) {
      throw new Error('[react-google-places-autocomplete]: Google script not loaded');
    }
    if (!window.google.maps) {
      throw new Error('[react-google-places-autocomplete]: Google maps script not loaded');
    }
    if (!window.google.maps.places) {
      throw new Error('[react-google-places-autocomplete]: Google maps places script not loaded');
    }

    setAutocompleteService(new window.google.maps.places.AutocompleteService());
  };

  const getLocations = useCallback(
    async (value: string) =>
      new Promise<Option[]>(resolve => {
        if (autocompleteService && value.length) {
          const inputValue: google.maps.places.AutocompletionRequest = {
            input: value
          };
          autocompleteService.getPlacePredictions(inputValue).then(data => {
            const validPredictions = filterLocationsToPostalCodeOrLower(data.predictions);
            if (validPredictions.length > 0) {
              resolve(
                validPredictions
                  .map(
                    prediction =>
                      ({
                        disabled: false,
                        imageName: undefined,
                        label: prediction.description,
                        value: prediction.place_id
                      }) as Option
                  )
                  .concat([googleOption])
              );
            } else {
              resolve(
                [
                  {
                    disabled: true,
                    imageName: undefined,
                    label: noResults,
                    value: 'noResults'
                  } as Option
                ].concat([googleOption])
              );
            }
          });
        } else {
          resolve(
            [
              {
                disabled: false,
                imageName: undefined,
                label: currentLocation?.label ?? '',
                value: currentLocation?.value ?? ''
              } as Option
            ].concat([googleOption])
          );
        }
      }),
    [autocompleteService, currentLocation?.label, currentLocation?.value, googleOption, noResults]
  );

  const debouncedLoadOptions = debounce(getLocations, 500, {
    leading: true
  });

  const defaultTemplateOptions = useMemo(() => {
    if (currentLocation) {
      return [currentLocation, googleOption];
    }
    return [googleOption];
  }, [currentLocation, googleOption]);

  useEffect(() => {
    if (import.meta.env.VITE_GOOGLE_PLACES_API_KEY) {
      const load = async () => {
        try {
          if (!window.google || !window.google.maps || !window.google.maps.places) {
            await new Loader({
              ...{ libraries: ['places'] },
              apiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY ?? ''
            }).load();
          }
          handleSetService();
        } catch {
          throw new Error('Failed to load google places API');
        }
      };
      load();
    }
  }, []);

  if (!autocompleteService) {
    return null;
  }

  return (
    <AsyncSelect<FormData>
      {...asyncSelectProps}
      control={control}
      defaultOptions={defaultTemplateOptions}
      error={error}
      label={label}
      name={name}
      onSearch={debouncedLoadOptions}
      onSelection={option => {
        setCurrentLocation({ ...option });
      }}
      required={required}
      subLabel={subLabel}
      value={currentLocation}
    />
  );
};

export default LocationAutocompleteInput;
